import { useMemo } from 'react';
import { usePermissionsStore } from '@/shared/store/permissionsStore';
import type { PermLevel } from './catalog';

const RANK: Record<PermLevel, number> = { hidden: 0, read: 1, write: 2 };

export function useEffectiveLevel(node: string | undefined | null): PermLevel {
  return usePermissionsStore((s) => (node ? s.effective[node] : 'write') ?? 'hidden');
}

/** Visible at all (read or write). Hidden nodes return false. */
export function useCanRead(node: string | undefined | null): boolean {
  const level = useEffectiveLevel(node);
  return RANK[level] >= RANK.read;
}

/** Write-capable on this node (counts +New / Save / Delete). */
export function useCanWrite(node: string | undefined | null): boolean {
  const level = useEffectiveLevel(node);
  return level === 'write';
}

/** True if the user explicitly cannot see this node. */
export function useIsHidden(node: string | undefined | null): boolean {
  const level = useEffectiveLevel(node);
  return level === 'hidden';
}

/** Compare an arbitrary level value to a required minimum. */
export function levelMeets(actual: PermLevel | undefined, required: PermLevel): boolean {
  if (!actual) return false;
  return RANK[actual] >= RANK[required];
}

/** Memoized lookup against a fixed list of nodes, returns one level per node. */
export function useLevels(nodes: ReadonlyArray<string>): Record<string, PermLevel> {
  const effective = usePermissionsStore((s) => s.effective);
  return useMemo(() => {
    const out: Record<string, PermLevel> = {};
    for (const n of nodes) out[n] = effective[n] ?? 'hidden';
    return out;
  }, [nodes, effective]);
}
